import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';

import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibChipAction } from '@chewy/kib-chips-react';

import { ArrowIcon } from '@src/components/icons/arrow';
import { FilledCheckIcon } from '@src/components/icons/check';

import { useRouteUtils } from '@src/hooks/use-route-utils.hook';

import WindowService from '@src/services/window.service';

export default function Plans({
  onFetchPlansClicked,
  onLinkClicked,
  onSectionView,
}: {
  onFetchPlansClicked: (label: string, value: string) => void;
  onLinkClicked: (
    label: string,
    value: string,
    extraData: Record<string, string>,
  ) => void;
  onSectionView: (
    label: string,
    value: string,
    extraData?: Record<string, string>,
  ) => void;
}) {
  const [plansSectionRef, plansSectionInview] = useInView({
    triggerOnce: true,
  });
  const [fetchPlansRef, fetchPlansInView] = useInView({
    triggerOnce: true,
  });

  const wellnessCoverageList = [
    <>Annual Check-Ups</>,
    <>Vaccines</>,
    <>Prescription Meds for Flea, Tick, & Heartworm</>,
    <>Live Vet Access</>,
  ];

  const { getInternalPath } = useRouteUtils();

  wellnessCoverageList.splice(
    2,
    0,
    ...[<>Dental Cleaning</>, <>Spaying & Neutering</>],
  );

  useEffect(() => {
    if (plansSectionInview) {
      onSectionView('seeInsurancePlans', 'See Insurance Plans');
    }
  }, [plansSectionInview]);

  useEffect(() => {
    if (fetchPlansInView) {
      onSectionView('middleFetchPlans', 'Fetch Plans');
    }
  }, [fetchPlansInView]);

  return (
    <section id="plans" className="pi-careplus__plans" ref={plansSectionRef}>
      <div className="pi-careplus__plans--content">
        <h2 className="pi-careplus__plans--title">
          You have options.{' '}
          <span>
            So does your pet.
            <small className="pi-careplus__plans--title-annotation">2</small>
          </span>
        </h2>
        <p className="pi-careplus__plans--text">
          Pick a CarePlus wellness or insurance plan that fits your budget and
          pet&apos;s needs, or bundle them for comprehensive coverage
          <span>
            . Enroll Multiple Pets and get up to 10% off your premiums.
          </span>
        </p>
      </div>
      <ul className="pi-careplus__plans--cards">
        <li className="pi-careplus__plans--card pi-careplus__plans--card-0 pi-careplus__plans--wellness-card">
          <h3 className="pi-careplus__plans--subtitle">
            Wellness Plans{' '}
            <span className="pi-careplus__plans--subtitle">
              for Everyday Health
            </span>
          </h3>
          <p className="pi-careplus__plans--subtext">
            Our wellness plans are designed to keep your pet healthier with
            coverage for annual check-ups, vaccines, and more.
          </p>
          <p className="pi-careplus__plans--subtitle-alt">
            <strong>Covers things like:</strong>
          </p>
          <ul className="pi-careplus__plans--list">
            {wellnessCoverageList.map((item, index) => (
              <li
                key={`wellness-plan--listitem-${index}`}
                className="pi-careplus__plans--listitem"
              >
                <FilledCheckIcon fill="#1c49c2" />
                <p className="pi-careplus__plans--subtext-alt">{item}</p>
              </li>
            ))}
          </ul>
          <div className="pi-careplus__plans--footer">
            <p className="pi-careplus__plans--text-alt">
              Starting at $20/month
            </p>
          </div>
        </li>
        <li className="pi-careplus__plans--card pi-careplus__plans--card-1 pi-careplus__plans--insurance-card">
          <h3 className="pi-careplus__plans--subtitle">
            Insurance Plans{' '}
            <span className="pi-careplus__plans--subtitle">
              for Accidents and Illnesses
            </span>
          </h3>
          <p className="pi-careplus__plans--subtext">
            Our insurance plans provide you with financial protection from
            unexpected accidents and illnesses so nothing gets in the way of
            caring for your pet when they need it most.
          </p>
          <p className="pi-careplus__plans--subtitle-alt">
            <strong>Covers things like:</strong>
          </p>
          <ul className="pi-careplus__plans--list">
            {[
              <>Accidents & Emergency Care</>,
              <>Serious Illness & Chronic Conditions</>,
              <>Prescription Medicine & Supplements</>,
              <>Live Vet Access</>,
            ].map((item, index) => (
              <li
                key={`insurance-plan--listitem-${index}`}
                className="pi-careplus__plans--listitem"
              >
                <FilledCheckIcon />
                <p className="pi-careplus__plans--subtext-alt">{item}</p>
              </li>
            ))}
          </ul>
          <div className="pi-careplus__plans--footer">
            <>
              <h3 className="pi-careplus__plans--title-alt-lemonade">
                A plan for every budget:
              </h3>
              <p className="pi-careplus__plans--text-alt">
                Starting at $20/month
                <em>National average for puppies and kittens</em>
              </p>
            </>
          </div>
        </li>
      </ul>

      <p className="pi-careplus__plans--text-lemonade">
        <small className="pi-careplus__plans--text-lemonade-annotation">
          2
        </small>
        Coverage, limits and discounts may vary by state and plan.
      </p>

      <div className="pi-careplus__plans--fetch" ref={fetchPlansRef}>
        <KibButtonNew
          onClick={() => {
            onFetchPlansClicked('middleFetchPlans', 'Fetch Plans');
          }}
          size="large"
        >
          Fetch Plans
        </KibButtonNew>

        <hr className="divider" />

        <div className="pi-careplus__quote--skip">
          <span className="pi-careplus__quote--legend">Already enrolled?</span>
          <div className="pi-careplus__quote--action">
            <KibChipAction
              icon={<ArrowIcon />}
              variant="transparent"
              href={getInternalPath(`${WindowService.base}/policies/dashboard`)}
              onClick={() => {
                onLinkClicked('middleManageYourPolicy', 'Manage Your Policy', {
                  url: getInternalPath(
                    `${WindowService.base}/policies/dashboard`,
                  ),
                });
              }}
            >
              Manage Your Policy
            </KibChipAction>
          </div>
        </div>
      </div>
    </section>
  );
}
